import { useState } from "react";
import type { ScopedThreadRef } from "@amoreg/contracts";
import { ChevronDown, Download, Eye } from "lucide-react";
import type { MarkdownFileLinkMeta } from "~/markdown-links";
import { useRightPanelStore } from "~/rightPanelStore";
import { usePrimaryEnvironmentId } from "~/state/environments";
import { useDocumentStores } from "~/state/documentStores";
import { useWorkspaceFileDownload } from "~/assets/useWorkspaceFileDownload";
import { Button } from "~/components/ui/button";
import { Menu, MenuTrigger, MenuPopup, MenuItem, MenuSeparator } from "~/components/ui/menu";
import { prepareWorkspaceFileExport } from "./workspaceFileExport";
import { OpenInDocumentStoreMenuItem } from "./OpenInDocumentStoreButton";

type Props = { cwd: string; threadRef: ScopedThreadRef; file: MarkdownFileLinkMeta };

export function ReplyFileActions(props: Props) {
  const [open, setOpen] = useState(false);
  return (
    <Menu open={open} onOpenChange={setOpen}>
      <MenuTrigger
        render={
          <Button
            size="sm"
            variant="outline"
            className="reply-file-open h-8 gap-1.5 rounded-xl px-3 text-sm font-normal sm:h-8 sm:text-sm"
            aria-label={`Open in options for ${props.file.basename}`}
          />
        }
      >
        Open in{" "}
        <ChevronDown className={`reply-files-chevron size-3.5 ${open ? "rotate-180" : ""}`} />
      </MenuTrigger>
      <MenuPopup align="end">{open ? <ReplyFileMenuItems {...props} /> : null}</MenuPopup>
    </Menu>
  );
}

/** Mount on demand: old replies do not initiate asset or integration requests. */
function ReplyFileMenuItems({ threadRef, file, cwd }: Props) {
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const stores = useDocumentStores(
    primaryEnvironmentId === threadRef.environmentId ? threadRef.threadId : "",
  );
  const download = useWorkspaceFileDownload(threadRef);
  return (
    <>
      <MenuItem
        onClick={() =>
          useRightPanelStore.getState().openFile(threadRef, file.workspaceRelativePath!)
        }
      >
        <Eye className="size-5" strokeWidth={1.75} />
        <span className="leading-5">Preview</span>
      </MenuItem>
      {stores.map((store) => (
        <OpenInDocumentStoreMenuItem
          key={store.id}
          store={store}
          threadId={threadRef.threadId}
          path={file.filePath}
          fileName={file.basename}
          beforeExport={() =>
            prepareWorkspaceFileExport(threadRef.environmentId, cwd, file.workspaceRelativePath!)
          }
        />
      ))}
      <MenuSeparator />
      <MenuItem onClick={() => download(file.workspaceRelativePath!)}>
        <Download className="size-5" strokeWidth={1.75} />
        <span className="leading-5">Download original</span>
      </MenuItem>
    </>
  );
}
